/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import { Modal, notification, Button, Table, Spin, Card, Tabs, ConfigProvider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../../redux/PQTlogDetails/axiosConfig1';

const { TabPane } = Tabs;

const emptyBundle = () => ({
  materials: [],
  weather: [],
  inspectionEquipment: [],
});

const parseMwResponse = (res) => {
  const body = res?.data;
  const inner = body?.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return {
      materials: Array.isArray(inner.materials) ? inner.materials : [],
      weather: Array.isArray(inner.weather) ? inner.weather : [],
      inspectionEquipment: Array.isArray(inner.inspectionEquipment)
        ? inner.inspectionEquipment
        : [],
    };
  }
  return emptyBundle();
};

const fmtDate = (v) => (v ? moment(v).format('DD-MMM-YYYY') : '—');
const fmtDateTime = (v) => (v ? moment(v).format('DD-MMM-YYYY HH:mm') : '—');
const fmtCell = (v) => (v == null || v === '' ? '—' : String(v));

const MATERIALS_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'batchDescription',
    key: 'batchDescription',
    ellipsis: true,
    render: fmtCell,
  },
  {
    title: 'BATCH Certificate No',
    dataIndex: 'batchNumber',
    key: 'batchNumber',
    ellipsis: true,
    render: fmtCell,
  },
  {
    title: 'Expiry date',
    dataIndex: 'expirationDate',
    key: 'expirationDate',
    width: 140,
    render: fmtDate,
  },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const WEATHER_COLUMNS = [
  { title: 'Condition', dataIndex: 'condition', key: 'condition', ellipsis: true, render: fmtCell },
  {
    title: 'Temperature (°C)',
    dataIndex: 'temperature',
    key: 'temperature',
    width: 140,
    render: fmtCell,
  },
  { title: 'Humidity (%)', dataIndex: 'humidity', key: 'humidity', width: 120, render: fmtCell },
  { title: 'Dew point', dataIndex: 'dewPoint', key: 'dewPoint', width: 100, render: fmtCell },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const EQUIPMENT_COLUMNS = [
  { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true, render: fmtCell },
  {
    title: 'Serial number',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    ellipsis: true,
    render: fmtCell,
  },
  { title: 'Expiry date', dataIndex: 'expiryDate', key: 'expiryDate', width: 140, render: fmtDate },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const MaterialsWeatherInspectionModal = ({ selectedSOW, logDateRange, onViewDetails }) => {
  const [isMwDetailsModalVisible, setIsMwDetailsModalVisible] = useState(false);
  const [mwBundle, setMwBundle] = useState(emptyBundle);
  const [mwLoading, setMwLoading] = useState(false);
  const [mwDetailsTab, setMwDetailsTab] = useState('environment');

  const fetchMwInspectionEquipment = useCallback(
    async (from, to) => {
      const sowId = selectedSOW?.id;
      if (!sowId || !from || !to) return false;
      setMwLoading(true);
      try {
        const fromDate = moment(from).format('YYYY-MM-DD');
        const toDate = moment(to).format('YYYY-MM-DD');
        const res = await axiosConfig.get('/pqt/sow/materials-weather-inspection-equipment', {
          params: { sowId, fromDate, toDate },
        });
        const bundle = parseMwResponse(res);
        setMwBundle(bundle);
        if (
          bundle.materials.length === 0 &&
          bundle.weather.length === 0 &&
          bundle.inspectionEquipment.length === 0
        ) {
          notification.info({
            message: 'No records',
            description: 'No materials, weather, or inspection equipment for this range.',
          });
        }
        return true;
      } catch (error) {
        console.error('materials-weather-inspection-equipment:', error);
        setMwBundle(emptyBundle());
        notification.error({
          message: 'Failed to load inspection data',
          description: error?.message || error?.error || 'Please try again later.',
        });
        return false;
      } finally {
        setMwLoading(false);
      }
    },
    [selectedSOW]
  );

  const handleViewDetailsClick = async () => {
    if (!selectedSOW?.id) {
      notification.warning({
        message: 'Select a pipeline',
        description: 'Choose a SOW (pipeline) before viewing details.',
      });
      return;
    }
    const from = logDateRange?.fromDate;
    const to = logDateRange?.toDate;
    if (!from || !to) {
      notification.warning({
        message: 'Select a date on the calendar',
        description:
          'Open a month or year on the calendar and pick a day (or month) so the log table loads. Dates from that view are used for materials, weather, and inspection equipment.',
      });
      return;
    }
    if (moment(to, 'YYYY-MM-DD').isBefore(moment(from, 'YYYY-MM-DD'), 'day')) {
      notification.warning({
        message: 'Invalid range',
        description: 'End date must be on or after start date.',
      });
      return;
    }
    setMwBundle(emptyBundle());
    setMwDetailsTab('environment');
    onViewDetails?.();
    setIsMwDetailsModalVisible(true);
    const ok = await fetchMwInspectionEquipment(from, to);
    if (!ok) {
      setIsMwDetailsModalVisible(false);
    }
  };

  const handleMwDetailsClose = () => {
    setIsMwDetailsModalVisible(false);
    setMwBundle(emptyBundle());
    setMwDetailsTab('environment');
  };

  /** `pipeline-table` matches heat profile import; no `h-100` so global `.ant-table-wrapper` height rules do not fight layout. */
  const tableProps = {
    pagination: false,
    className: 'pipeline-table',
    size: 'small',
    style: { background: '#1a1a1a', width: '100%', minWidth: 0, maxWidth: '100%' },
    scroll: {
      y: 'min(480px, calc(100vh - 320px))',
    },
  };

  return (
    <>
      <style>
        {`
          .mw-inspection-details-modal .ant-modal-content {
            background: #1a1a1a;
          }
          .mw-inspection-details-modal .ant-modal-header {
            display: none !important;
          }
          .mw-inspection-details-modal .ant-modal {
            width: 98vw !important;
            max-width: 98vw;
            padding-bottom: 0;
          }
          .mw-inspection-details-modal .ant-modal-content {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          .mw-inspection-details-modal .ant-modal-body {
            background: #1a1a1a;
            color: #fff;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            max-height: calc(100vh - 120px);
            overflow: auto;
          }
          .mw-inspection-details-modal .mw-inspection-details-table-root {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }
          .mw-inspection-details-modal .ant-spin-nested-loading,
          .mw-inspection-details-modal .ant-spin-container {
            width: 100%;
            max-width: 100%;
          }
          .mw-inspection-details-modal .mw-inspection-details-card {
            width: 100%;
            max-width: 100%;
            background: #1a1a1a !important;
            border: none !important;
            box-shadow: none !important;
          }
          .mw-inspection-details-modal .mw-inspection-details-card .ant-card-body {
            background: #1a1a1a;
            width: 100%;
            box-sizing: border-box;
            padding: 0 !important;
          }
          .mw-inspection-details-modal .pipeline-table.ant-table-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            background: #1a1a1a;
            padding: 0 !important;
            height: auto !important;
          }
          /* Global antd-custom sets height 100% / 96% here and breaks full-width scroll tables */
          .mw-inspection-details-modal .pipeline-table.ant-table-wrapper .ant-spin-nested-loading,
          .mw-inspection-details-modal .pipeline-table.ant-table-wrapper .ant-spin-container,
          .mw-inspection-details-modal .pipeline-table .ant-table {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            background: #1a1a1a !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-container {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-scroll {
            width: 100% !important;
            max-width: 100% !important;
          }
          /* Do not force width on .ant-table-header / .ant-table-body — breaks antd scrollbar sync and causes a gap. */
          /* Fixed header reserves padding-bottom for a vertical scrollbar; overlay scrollbars leave an empty strip */
          .mw-inspection-details-modal .pipeline-table .ant-table-header {
            padding-bottom: 0 !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-scroll > .ant-table-body {
            margin-top: 0 !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-header table,
          .mw-inspection-details-modal .pipeline-table .ant-table-body table {
            border-collapse: collapse !important;
            border-spacing: 0 !important;
          }
          /* Scroll layer defaults to near-black; match list modal (#1a1a1a) */
          .mw-inspection-details-modal .pipeline-table .ant-table-container,
          .mw-inspection-details-modal .pipeline-table .ant-table-content,
          .mw-inspection-details-modal .pipeline-table .ant-table-body,
          .mw-inspection-details-modal .pipeline-table .ant-table-body-inner {
            background: #1a1a1a !important;
          }
          /* rc-table / antd internal measure row — must stay in DOM for layout but should not show as a data row */
          .mw-inspection-details-modal .pipeline-table tr.ant-table-measure-row,
          .mw-inspection-details-modal .pipeline-table tr.rc-table-measure-row {
            height: 0 !important;
            font-size: 0 !important;
            line-height: 0 !important;
            border: none !important;
          }
          .mw-inspection-details-modal .pipeline-table tr.ant-table-measure-row td,
          .mw-inspection-details-modal .pipeline-table tr.rc-table-measure-row td {
            height: 0 !important;
            padding: 0 !important;
            border: none !important;
            overflow: hidden !important;
            line-height: 0 !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr.ant-table-row {
            background: transparent !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr.ant-table-row:nth-child(even) {
            background: transparent !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-container,
          .mw-inspection-details-modal .pipeline-table .ant-table-content {
            width: 100% !important;
            border: none !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-content > table,
          .mw-inspection-details-modal .pipeline-table .ant-table table {
            width: 100% !important;
            border: none !important;
            table-layout: fixed;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-thead > tr > th,
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr > td {
            border-right: none !important;
            border-left: none !important;
            border-top: none !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-thead > tr > th::before,
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr > td::before {
            display: none !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-thead > tr > th {
            background: #2d2d2d !important;
            border-bottom: 1px solid #404040 !important;
            color: #ff4d4f !important;
            font-weight: 700 !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-thead > tr > th .ant-table-column-title {
            font-weight: 700 !important;
            color: #ff4d4f !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr > td {
            background: #1a1a1a !important;
            border-bottom: 1px solid #404040 !important;
            color: #fff !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-tbody > tr:hover > td {
            background: #2d2d2d !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-placeholder .ant-table-cell {
            border: none !important;
          }
          .mw-inspection-details-modal .pipeline-table .ant-table-placeholder .ant-table-cell,
          .mw-inspection-details-modal .pipeline-table .ant-empty {
            background: transparent;
            color: rgba(255, 255, 255, 0.65);
          }
          .mw-inspection-details-modal .pipeline-table .ant-empty-description {
            color: rgba(255, 255, 255, 0.65);
          }
          .mw-inspection-details-modal .mw-inspection-tabs {
            width: 100%;
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-content-holder,
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-tabpane {
            width: 100%;
            max-width: 100%;
          }
          .mw-inspection-details-modal .mw-inspection-tabs.ant-tabs-top > .ant-tabs-nav {
            margin-bottom: 8px;
            padding: 0 16px;
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-nav::before {
            border-bottom-color: #404040;
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-tab {
            color: rgba(255, 255, 255, 0.65);
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-tab:hover {
            color: rgba(255, 255, 255, 0.95);
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #ff4d4f;
            font-weight: 600;
          }
          .mw-inspection-details-modal .mw-inspection-tabs .ant-tabs-ink-bar {
            background: #ff4d4f;
          }
        `}
      </style>
      <Button type="default" onClick={handleViewDetailsClick} loading={mwLoading}>
        View Details
      </Button>

      <Modal
        width="98vw"
        className="heat-profile-modal pipeline-modal"
        wrapClassName="mw-inspection-details-modal"
        title=""
        closable={false}
        visible={isMwDetailsModalVisible}
        onCancel={handleMwDetailsClose}
        footer={null}
        destroyOnClose
        style={{ top: 10 }}
        bodyStyle={{
          background: '#1a1a1a',
          padding: '8px 0 16px',
          maxHeight: 'calc(100vh - 120px)',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: -6,
            marginBottom: 6,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleMwDetailsClose}
            style={{
              color: '#ff4d4f',
              padding: '2px 6px 2px 2px',
              height: 'auto',
              lineHeight: 1,
            }}
            aria-label="Close details"
          />
        </div>
        <ConfigProvider
          getPopupContainer={(triggerNode) =>
            triggerNode?.closest?.('.ant-modal') || document.body
          }
        >
          <div className="mw-inspection-details-table-root">
            <Spin spinning={mwLoading}>
              <Tabs
                activeKey={mwDetailsTab}
                onChange={setMwDetailsTab}
                className="mw-inspection-tabs"
                destroyInactiveTabPane={false}
              >
              <TabPane
                tab={`Environmental Conditions (${mwBundle.weather.length})`}
                key="environment"
              >
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={WEATHER_COLUMNS}
                    dataSource={mwBundle.weather.map((row) => ({ ...row, key: row.id }))}
                    locale={{ emptyText: 'No environmental condition records' }}
                  />
                </Card>
              </TabPane>
              <TabPane tab={`Materials (${mwBundle.materials.length})`} key="materials">
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={MATERIALS_COLUMNS}
                    dataSource={mwBundle.materials.map((row) => ({ ...row, key: row.id }))}
                    locale={{ emptyText: 'No materials' }}
                  />
                </Card>
              </TabPane>
              <TabPane
                tab={`Inspection equipment (${mwBundle.inspectionEquipment.length})`}
                key="equipment"
              >
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={EQUIPMENT_COLUMNS}
                    dataSource={mwBundle.inspectionEquipment.map((row) => ({
                      ...row,
                      key: row.id,
                    }))}
                    locale={{ emptyText: 'No inspection equipment' }}
                  />
                </Card>
              </TabPane>
            </Tabs>
            </Spin>
          </div>
        </ConfigProvider>
      </Modal>
    </>
  );
};

export default MaterialsWeatherInspectionModal;
